import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import apiClient from '../api/client';
import { useTheme } from '../contexts/ThemeContext';
import { Plus, Check, X } from 'lucide-react';

const Leave = () => {
  const { isDarkMode } = useTheme();
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [leaveBalance, setLeaveBalance] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [newRequest, setNewRequest] = useState({
    employee_id: '',
    leave_type: 'casual',
    start_date: '',
    end_date: '',
    days: 1,
    reason: ''
  });

  useEffect(() => {
    fetchLeaveData();
  }, []);

  const fetchLeaveData = async () => {
    try {
      const [requestsRes, empRes] = await Promise.all([
        apiClient.get('/api/leave/requests'),
        apiClient.get('/api/employees')
      ]);
      setLeaveRequests(requestsRes.data);
      setEmployees(empRes.data);
    } catch (error) {
      console.error('Error fetching leave data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getEmployeeName = (empId) => {
    const emp = employees.find(e => e.id === empId);
    return emp ? emp.full_name : empId;
  };

  const handleRequestLeave = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post('/api/leave/request', newRequest);
      fetchLeaveData();
      setShowRequestModal(false);
      setNewRequest({
        employee_id: '',
        leave_type: 'casual',
        start_date: '',
        end_date: '',
        days: 1,
        reason: ''
      });
    } catch (error) {
      console.error('Error requesting leave:', error);
      alert('Error requesting leave');
    }
  };

  const handleApproveReject = async (requestId, status) => {
    try {
      await apiClient.put(`/api/leave/requests/${requestId}`, { status, remarks: '' });
      fetchLeaveData();
    } catch (error) {
      console.error('Error updating leave request:', error);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full">
          <div className="loading-spinner"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page-header flex items-center justify-between">
        <div>
          <h1 className={`text-3xl font-bold ${isDarkMode ? 'text-[hsl(210_15%_92%)]' : 'text-gray-900'}`}>Leave Management</h1>
          <p className={`mt-1 ${isDarkMode ? 'text-[hsl(215_10%_70%)]' : 'text-gray-600'}`}>Manage leave requests and balances</p>
        </div>
        <button
          onClick={() => setShowRequestModal(true)}
          data-testid="request-leave-button"
          className="flex items-center gap-2 bg-[#00B8B8] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#0ED2D2]"
        >
          <Plus size={20} />
          Request Leave
        </button>
      </div>

      {/* Leave Requests Table */}
      <div className="table-container">
        <table className="w-full">
          <thead className="table-header">
            <tr>
              <th className="text-left p-4">Employee</th>
              <th className="text-left p-4">Leave Type</th>
              <th className="text-left p-4">Start Date</th>
              <th className="text-left p-4">End Date</th>
              <th className="text-left p-4">Days</th>
              <th className="text-left p-4">Status</th>
              <th className="text-left p-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {leaveRequests.length === 0 ? (
              <tr>
                <td colSpan="7" className="text-center p-8 text-[#737373]">
                  No leave requests found
                </td>
              </tr>
            ) : (
              leaveRequests.map((req) => (
                <tr key={req.id} className="border-t border-[#E5E5E5] hover:bg-[#F5F5F5]">
                  <td className="p-4 font-medium text-[#262626]">{getEmployeeName(req.employee_id)}</td>
                  <td className="p-4">
                    <span className="capitalize text-[#737373]">{req.leave_type}</span>
                  </td>
                  <td className="p-4 text-[#737373]">{req.start_date}</td>
                  <td className="p-4 text-[#737373]">{req.end_date}</td>
                  <td className="p-4 font-mono font-medium text-[#262626]">{req.days}</td>
                  <td className="p-4">
                    <span className={`status-badge ${req.status === 'approved' ? 'status-approved' : req.status === 'rejected' ? 'status-rejected' : 'status-pending'}`}>
                      {req.status}
                    </span>
                  </td>
                  <td className="p-4">
                    {req.status === 'pending' && (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleApproveReject(req.id, 'approved')}
                          data-testid={`approve-leave-${req.id}`}
                          className="p-2 text-[#10B981] hover:bg-[#10B981]/10 rounded"
                        >
                          <Check size={18} />
                        </button>
                        <button
                          onClick={() => handleApproveReject(req.id, 'rejected')}
                          data-testid={`reject-leave-${req.id}`}
                          className="p-2 text-[#EF4444] hover:bg-[#EF4444]/10 rounded"
                        >
                          <X size={18} />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Request Leave Modal */}
      {showRequestModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowRequestModal(false)}>
          <div className="bg-white rounded-xl p-6 max-w-md w-full m-4" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-2xl font-bold text-[#262626] mb-6">Request Leave</h2>
            <form onSubmit={handleRequestLeave} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#262626] mb-2">Employee ID*</label>
                <input
                  type="text"
                  required
                  value={newRequest.employee_id}
                  onChange={(e) => setNewRequest({...newRequest, employee_id: e.target.value})}
                  data-testid="leave-employee-id-input"
                  className="w-full px-4 py-2 border border-[#E5E5E5] rounded-lg focus:ring-2 focus:ring-[#7EC845]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#262626] mb-2">Leave Type*</label>
                <select
                  required
                  value={newRequest.leave_type}
                  onChange={(e) => setNewRequest({...newRequest, leave_type: e.target.value})}
                  data-testid="leave-type-select"
                  className="w-full px-4 py-2 border border-[#E5E5E5] rounded-lg focus:ring-2 focus:ring-[#7EC845]"
                >
                  <option value="casual">Casual</option>
                  <option value="sick">Sick</option>
                  <option value="earned">Earned</option>
                  <option value="paid">Paid Leave</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#262626] mb-2">Start Date*</label>
                  <input
                    type="date"
                    required
                    value={newRequest.start_date}
                    onChange={(e) => setNewRequest({...newRequest, start_date: e.target.value})}
                    data-testid="leave-start-date-input"
                    className="w-full px-4 py-2 border border-[#E5E5E5] rounded-lg focus:ring-2 focus:ring-[#7EC845]"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#262626] mb-2">End Date*</label>
                  <input
                    type="date"
                    required
                    value={newRequest.end_date}
                    onChange={(e) => setNewRequest({...newRequest, end_date: e.target.value})}
                    data-testid="leave-end-date-input"
                    className="w-full px-4 py-2 border border-[#E5E5E5] rounded-lg focus:ring-2 focus:ring-[#7EC845]"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#262626] mb-2">Days*</label>
                <input
                  type="number"
                  required
                  min="0.5"
                  step="0.5"
                  value={newRequest.days}
                  onChange={(e) => setNewRequest({...newRequest, days: parseFloat(e.target.value)})}
                  data-testid="leave-days-input"
                  className="w-full px-4 py-2 border border-[#E5E5E5] rounded-lg focus:ring-2 focus:ring-[#7EC845]"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#262626] mb-2">Reason*</label>
                <textarea
                  required
                  value={newRequest.reason}
                  onChange={(e) => setNewRequest({...newRequest, reason: e.target.value})}
                  data-testid="leave-reason-input"
                  rows="3"
                  className="w-full px-4 py-2 border border-[#E5E5E5] rounded-lg focus:ring-2 focus:ring-[#7EC845]"
                ></textarea>
              </div>
              <div className="flex gap-4 justify-end mt-6">
                <button
                  type="button"
                  onClick={() => setShowRequestModal(false)}
                  className="px-6 py-2 border border-[#E5E5E5] rounded-lg text-[#262626] hover:bg-[#F5F5F5]"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  data-testid="submit-leave-request"
                  className="px-6 py-2 bg-[#7EC845] text-white rounded-lg hover:bg-[#6EB835]"
                >
                  Submit Request
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Leave;